// Dokumentdetail: links AbPdfViewer (Canvas + Fundstellen-Overlay + Klick-Uebernahme) mit dem
// E-Mail-Kontext darueber, rechts Kopfdaten, Abgleichs-/Pflichtfeld-/Supersede-Panels, Positions-
// tabelle (AbLineTable) mit Inline-Edit, Warnungen, Verlauf (inkl. Melde-Historie) und eine sticky
// Aktionsleiste (Freigeben/Ablehnen/Neu verarbeiten/Loeschen/Melden). Umbau "UI-Paritaet" (Bauplan
// Agent FE-2) — Panels/PDF-Arbeitsplatz in components/detail/ bzw. components/pdf/ ausgelagert,
// damit diese Datei unter der 500-LOC-Regel bleibt.
//
// Props (Absprache Welle 1/2, s. BAUPLAN-AB-UI-PARITAET): { user, isDarkMode, t, documentId,
// onBack, onOpenDocument?, onOpenMail?, onOpenMapping?, showSuccess, showError }. Die optionalen
// onOpen*-Callbacks werden erst mit dem Shell-Umbau (Agent FE-1) verdrahtet — bis dahin werden die
// davon abhaengigen Buttons/Umschalter automatisch ausgeblendet (kein toter Handler).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, CheckCircle2, XCircle, RefreshCw, Trash2, AlertTriangle,
  PanelLeftClose, PanelLeftOpen, Building2, Hash, Calendar, LayoutTemplate,
} from 'lucide-react';
import {
  Card, SectionTitle, StatusBadge, btnPrimary, btnGhost, btnDanger,
  inputCls, fmtDate, ConfirmDialog, eventLabel,
} from './ui';
import AbLineTable from './AbLineTable';
import AbSuggestPanel from './AbSuggestPanel';
import AbCommitResult from './AbCommitResult';
import AbPdfViewer from './pdf/AbPdfViewer';
import { buildHighlights } from './pdf/wordMatch';
import AbSupersedeBanner from './detail/AbSupersedeBanner';
import AbCompareStatusPanel from './detail/AbCompareStatusPanel';
import AbMissingFieldsPanel from './detail/AbMissingFieldsPanel';
import AbEmailContextPanel from './detail/AbEmailContextPanel';
import AbSiblingSwitcher from './detail/AbSiblingSwitcher';
import AbReportButton from './detail/AbReportButton';
import AbReportHistory from './detail/AbReportHistory';
import AbRawTextPanel from './detail/AbRawTextPanel';
import AbPickHintBanner from './detail/AbPickHintBanner';
import {
  getDocument, getCompare, getCompareAmpl, getWords, patchLineField, addLine, deleteLine,
  reprocessDocument, approveDocument, rejectDocument, deleteDocument,
} from '../api';

const EDITABLE_STATES = ['needs_review', 'no_ruleset'];
const REPROCESSABLE_STATES = ['needs_review', 'no_ruleset', 'failed'];
// Statusmaschine: Ablehnen ist nur aus needs_review erlaubt (Backend antwortet sonst 409).
const REJECTABLE_STATES = ['needs_review'];

const AbDocumentDetail = ({
  user, isDarkMode, t, documentId, onBack, onOpenDocument, onOpenMail, onOpenMapping,
  showSuccess, showError,
}) => {
  const [doc, setDoc] = useState(null);
  const [compare, setCompare] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [htnBusy, setHtnBusy] = useState(false);
  const [htnChecked, setHtnChecked] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(null); // { kind, title, message, tone, onConfirm }
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // SAP-Commit-Ergebnis nach Freigabe (nur AB_COMMIT_TARGET=sap): { ok, groups } | { fourEyes, message }.
  const [commitResult, setCommitResult] = useState(null);

  // ---- PDF-Arbeitsplatz: Fundstellen + Klick-Uebernahme-Modus (Bauplan Punkt 1+2) ----
  const [words, setWords] = useState(null);
  const [activeField, setActiveField] = useState(null); // { lineNo, field } | null — PDF-Highlight-Ziel
  const [pickTarget, setPickTarget] = useState(null);    // { lineNo, field } | null — Klick-Uebernahme scharf
  const [fillSignal, setFillSignal] = useState(null);    // { lineNo, field, text, append, seq } | null
  const pickCountRef = useRef(0); // 0 = naechster Fund ersetzt, danach haengt jeder weitere Klick an
  const wordsLoadedForRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  // Ergebnis-Panel einer vorherigen Freigabe/PDF-Zustand darf nicht auf ein neu geoeffnetes Dokument "durchbluten".
  useEffect(() => {
    setCommitResult(null);
    setPickTarget(null);
    setActiveField(null);
    setFillSignal(null);
    setWords(null);
  }, [documentId]);

  const load = useCallback(async () => {
    try {
      const d = await getDocument(user, documentId);
      setDoc(d);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message);
      return;
    }
    try { setCompare(await getCompare(user, documentId)); }
    catch { setCompare(null); } // Abgleich ist fehler-tolerant, blockiert die Review nicht
  }, [user, documentId]);

  useEffect(() => {
    setHtnChecked(false);
    setCompareLoading(true);
    load().finally(() => setCompareLoading(false));
  }, [load]);

  // Wortboxen erst NACH dem Erstrender laden (lazy, blockiert die Review nicht) — je documentId nur einmal.
  useEffect(() => {
    if (!doc || !documentId || wordsLoadedForRef.current === documentId) return undefined;
    wordsLoadedForRef.current = documentId;
    let alive = true;
    getWords(user, documentId).then((w) => { if (alive) setWords(w); }).catch(() => { if (alive) setWords(null); });
    return () => { alive = false; };
  }, [doc, user, documentId]);

  // Esc beendet den Klick-Uebernahme-Modus global (auch wenn der Fokus gerade im PDF liegt).
  useEffect(() => {
    if (!pickTarget) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPickTarget(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickTarget]);

  const compareByLine = useMemo(() => {
    const m = new Map();
    if (Array.isArray(compare?.lines)) for (const l of compare.lines) m.set(l.lineNo, l);
    return m;
  }, [compare]);

  // Fundstellen (Wort-Boxen je Position/Feld) aus den extrahierten Werten + OCR-Wortboxen.
  const highlightMap = useMemo(() => buildHighlights(words, doc?.lines), [words, doc]);
  const highlights = useMemo(() => {
    const flat = [];
    for (const [lineNo, byField] of highlightMap) {
      for (const [field, boxes] of byField) {
        const id = `${lineNo}:${field}`;
        const byPage = new Map();
        for (const b of boxes) {
          const arr = byPage.get(b.page) || [];
          arr.push({ x0: b.x0, y0: b.y0, x1: b.x1, y1: b.y1 });
          byPage.set(b.page, arr);
        }
        for (const [page, boxesForPage] of byPage) flat.push({ id, page, boxes: boxesForPage, tone: 'found' });
      }
    }
    return flat;
  }, [highlightMap]);
  const activeHighlightId = activeField ? `${activeField.lineNo}:${activeField.field}` : null;

  const textMain = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const textMuted = isDarkMode ? 'text-white/55' : 'text-slate-500';
  const rowBorder = isDarkMode ? 'border-white/[0.08]' : 'border-slate-100';

  if (loadError) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className={btnGhost(isDarkMode)}>
          <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
        </button>
        <Card isDarkMode={isDarkMode}>
          <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('ab.detailLoadError')}: {loadError}</p>
          <button onClick={load} className={`${btnGhost(isDarkMode)} mt-3`}>{t('ab.refresh')}</button>
        </Card>
      </div>
    );
  }
  if (!doc) {
    return <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.loading')}</p>;
  }

  const editable = EDITABLE_STATES.includes(doc.status);
  const canApprove = doc.status === 'needs_review';
  const canReject = REJECTABLE_STATES.includes(doc.status);
  const canReprocess = REPROCESSABLE_STATES.includes(doc.status);
  const docWarnings = Array.isArray(doc.warnings) ? doc.warnings : [];
  const events = Array.isArray(doc.events) ? doc.events : [];

  const runAction = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) showSuccess?.(okMsg);
      await load();
    } catch (e) {
      showError?.(e.message);
    } finally {
      setBusy(false);
    }
  };

  const askConfirm = (kind, title, message, tone, onConfirm) =>
    setConfirmDialog({ kind, title, message, tone, onConfirm });

  // Eigener Handler statt runAction: bei AB_COMMIT_TARGET=sap muss die strukturierte
  // Commit-Antwort (Erfolg mit Gruppen / 409 mit Gruppen / 403 Vier-Augen) ausgewertet werden.
  const doApprove = async () => {
    setBusy(true);
    setCommitResult(null);
    try {
      const res = await approveDocument(user, doc.id);
      if (res?.commit?.target === 'sap') {
        setCommitResult({ ok: true, groups: Array.isArray(res.commit.groups) ? res.commit.groups : [] });
      }
      showSuccess?.(t('ab.approvedMsg'));
      await load();
    } catch (e) {
      if (e.status === 409 && Array.isArray(e.body?.groups)) {
        setCommitResult({ ok: false, groups: e.body.groups });
        showError?.(e.message || t('ab.commitFailedMsg'));
      } else if (e.status === 403) {
        setCommitResult({ fourEyes: true, message: e.message });
        showError?.(e.message);
      } else {
        showError?.(e.message);
      }
    } finally {
      setBusy(false);
      setConfirmDialog(null);
    }
  };
  const doReprocess = () => runAction(async () => {
    await reprocessDocument(user, doc.id);
    // OCR-Neulauf kann die Wortboxen aendern -> Fundstellen-Cache verwerfen und neu laden.
    wordsLoadedForRef.current = null;
    setWords(null);
  }, t('ab.reprocessedMsg')).finally(() => setConfirmDialog(null));
  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteDocument(user, doc.id);
      showSuccess?.(t('ab.deletedMsg'));
      onBack();
    } catch (e) {
      showError?.(e.message);
      setBusy(false);
    } finally {
      setConfirmDialog(null);
    }
  };
  const doReject = async () => {
    if (!rejectReason.trim()) { showError?.(t('ab.rejectReasonRequired')); return; }
    await runAction(() => rejectDocument(user, doc.id, rejectReason.trim()), t('ab.rejectedMsg'));
    setRejectOpen(false);
    setRejectReason('');
  };

  const runHtnCheck = async () => {
    setHtnBusy(true);
    try {
      setCompare(await getCompareAmpl(user, doc.id));
      setHtnChecked(true);
    } catch {
      /* best-effort: bei Fehler bleibt der bisherige Stand */
    } finally {
      setHtnBusy(false);
    }
  };

  // Fehler toasten UND weiterwerfen, damit EditableCell die Zelle zum Korrigieren offen laesst.
  const onSaveField = (lineNo, field, value) =>
    patchLineField(user, doc.id, lineNo, field, value).then(load).catch((e) => { showError?.(e.message); throw e; });
  const onDeleteLine = (lineNo) => runAction(() => deleteLine(user, doc.id, lineNo), t('ab.lineDeletedMsg'));
  const onAddLine = (payload) => runAction(() => addLine(user, doc.id, payload), t('ab.lineAddedMsg'));

  // ---- Klick-Uebernahme-Modus: Feld scharfschalten (Doppelklick/Edit-Icon) vs. nur hervorheben (Klick). ----
  const onHighlightField = (lineNo, field) => setActiveField({ lineNo, field });
  const onActivateField = (lineNo, field) => {
    setActiveField({ lineNo, field });
    setPickTarget({ lineNo, field });
    pickCountRef.current = 0;
  };
  const onDeactivateField = () => setPickTarget(null);
  const onWordClick = (word) => {
    if (!pickTarget) return;
    const text = (word?.t ?? '').trim();
    if (!text) return;
    setFillSignal({ lineNo: pickTarget.lineNo, field: pickTarget.field, text, append: pickCountRef.current > 0, seq: Date.now() });
    pickCountRef.current += 1;
  };

  return (
    <div className="space-y-4">
      {confirmDialog && (
        <ConfirmDialog
          isDarkMode={isDarkMode} t={t} busy={busy}
          title={confirmDialog.title} message={confirmDialog.message} tone={confirmDialog.tone}
          onConfirm={confirmDialog.onConfirm} onCancel={() => setConfirmDialog(null)}
        />
      )}
      {rejectOpen && (
        <ConfirmDialog
          isDarkMode={isDarkMode} t={t} busy={busy} tone="danger"
          title={t('ab.rejectDialogTitle')} confirmLabel={t('ab.reject')}
          onConfirm={doReject} onCancel={() => { setRejectOpen(false); setRejectReason(''); }}
        >
          <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('ab.rejectReasonPlaceholder')} rows={3}
            className={`${inputCls(isDarkMode)} w-full resize-none`} />
        </ConfirmDialog>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className={btnGhost(isDarkMode)}>
          <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
        </button>
        <h2 className={`text-lg font-bold truncate max-w-md ${textMain}`}>{doc.fileName}</h2>
        <StatusBadge status={doc.status} isDarkMode={isDarkMode} t={t} />
        {doc.ebeln && <span className={`text-xs font-mono ${textMuted}`}>{doc.ebeln}</span>}
        <AbSiblingSwitcher isDarkMode={isDarkMode} t={t} documentId={doc.id} siblings={doc.siblings} onOpenDocument={onOpenDocument} />
        <div className="flex-1" />
        {typeof onOpenMapping === 'function' && (
          <button onClick={() => onOpenMapping(doc.id)} className={btnGhost(isDarkMode)}>
            <span className="flex items-center gap-2"><LayoutTemplate className="w-4 h-4" />{t('abPdf.mappingButton')}</span>
          </button>
        )}
        <AbReportButton
          user={user} isDarkMode={isDarkMode} t={t} documentId={doc.id} reportedAt={doc.reportedAt} isAdmin={isAdmin}
          showSuccess={showSuccess} showError={showError} onChanged={load}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* LINKS: E-Mail-Kontext + PDF-Arbeitsplatz (ein-/ausklappbar) */}
        {pdfOpen ? (
          <div className="lg:col-span-2 space-y-4">
            <AbEmailContextPanel
              isDarkMode={isDarkMode} t={t} emailContext={doc.emailContext}
              mailMessageId={doc.mailMessageId} onOpenMail={onOpenMail}
            />
            <Card isDarkMode={isDarkMode}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <SectionTitle isDarkMode={isDarkMode}>{t('ab.pdfView')}</SectionTitle>
                <button className={btnGhost(isDarkMode)} onClick={() => setPdfOpen(false)} title={t('ab.pdfHide')}>
                  <PanelLeftClose className="w-4 h-4" />
                </button>
              </div>
              <AbPdfViewer
                user={user} documentId={doc.id} words={words} highlights={highlights}
                activeHighlightId={activeHighlightId} onWordClick={pickTarget ? onWordClick : null}
                clickMode={!!pickTarget} isDarkMode={isDarkMode} t={t}
              />
            </Card>
          </div>
        ) : (
          <div className="lg:col-span-1">
            <button className={`${btnGhost(isDarkMode)} w-full`} onClick={() => setPdfOpen(true)} title={t('ab.pdfShow')}>
              <span className="flex items-center gap-2"><PanelLeftOpen className="w-4 h-4" />{t('ab.pdfShow')}</span>
            </button>
          </div>
        )}

        {/* RECHTS: Kopfdaten, Abgleich, Positionen, Warnungen, Verlauf, Aktionen */}
        <div className={pdfOpen ? 'lg:col-span-3 space-y-4' : 'lg:col-span-4 space-y-4'}>
          {/* Sticky Aktionsleiste */}
          <div className="sticky top-4 z-10">
            <Card isDarkMode={isDarkMode} className={isDarkMode ? 'backdrop-blur-xl' : 'backdrop-blur-xl bg-white/95'}>
              <div className="flex flex-wrap items-center gap-2">
                {canApprove && (
                  <button disabled={busy} className={btnPrimary(isDarkMode)}
                    onClick={() => askConfirm('approve', t('ab.approveDialogTitle'), t('ab.approveDialogMsg'), 'primary', doApprove)}>
                    <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{t('ab.approve')}</span>
                  </button>
                )}
                {canReject && (
                  <button disabled={busy} className={btnDanger(isDarkMode)} onClick={() => setRejectOpen(true)}>
                    <span className="flex items-center gap-2"><XCircle className="w-4 h-4" />{t('ab.reject')}</span>
                  </button>
                )}
                {canReprocess && (
                  <button disabled={busy} className={btnGhost(isDarkMode)}
                    onClick={() => askConfirm('reprocess', t('ab.reprocessDialogTitle'), t('ab.reprocessDialogMsg'), 'primary', doReprocess)}>
                    <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4" />{t('ab.reprocess')}</span>
                  </button>
                )}
                <div className="flex-1" />
                <button disabled={busy} className={btnGhost(isDarkMode)}
                  onClick={() => askConfirm('delete', t('ab.deleteDialogTitle'), t('ab.deleteDialogMsg'), 'danger', doDelete)}>
                  <span className="flex items-center gap-2"><Trash2 className="w-4 h-4" />{t('common.delete')}</span>
                </button>
              </div>
            </Card>
          </div>

          {/* SAP-Commit-Ergebnis (nur AB_COMMIT_TARGET=sap: Erfolg mit Gruppen / 409 / 403 Vier-Augen) */}
          <AbCommitResult isDarkMode={isDarkMode} t={t} result={commitResult} onDismiss={() => setCommitResult(null)} />

          {/* Kein passendes Regelset: KI-Regelset-Vorschlag anbieten */}
          {doc.status === 'no_ruleset' && (
            <AbSuggestPanel
              user={user} isDarkMode={isDarkMode} t={t} documentId={doc.id} isAdmin={isAdmin}
              showSuccess={showSuccess} showError={showError}
            />
          )}

          {/* Panels oberhalb der Tabelle (Bauplan Punkt 3): Supersede -> Abgleich/HTN -> Pflichtfelder */}
          <AbSupersedeBanner isDarkMode={isDarkMode} t={t} newerAb={doc.newerAb} onOpenDocument={onOpenDocument} />
          <AbCompareStatusPanel
            isDarkMode={isDarkMode} t={t} compareLoading={compareLoading} compare={compare}
            docLineCount={doc.lines.length} htnBusy={htnBusy} htnChecked={htnChecked} onRunHtnCheck={runHtnCheck}
          />
          <AbMissingFieldsPanel isDarkMode={isDarkMode} t={t} lines={doc.lines} noRuleset={doc.status === 'no_ruleset'} />

          {/* Kopfdaten */}
          <Card isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>{t('ab.headerData')}</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className={`text-[11px] uppercase tracking-wide font-semibold mb-1 flex items-center gap-1 ${textMuted}`}><Building2 className="w-3.5 h-3.5" />{t('ab.field.supplier')}</div>
                <div className={`text-sm font-medium ${textMain}`}>{doc.supplierName || doc.supplierId || t('ab.supplierUnknown')}</div>
              </div>
              <div>
                <div className={`text-[11px] uppercase tracking-wide font-semibold mb-1 flex items-center gap-1 ${textMuted}`}><Hash className="w-3.5 h-3.5" />{t('ab.field.ebeln')}</div>
                <div className={`text-sm font-medium font-mono ${textMain}`}>{doc.ebeln || '—'}</div>
              </div>
              <div>
                <div className={`text-[11px] uppercase tracking-wide font-semibold mb-1 flex items-center gap-1 ${textMuted}`}><Calendar className="w-3.5 h-3.5" />{t('ab.colCreated')}</div>
                <div className={`text-sm font-medium ${textMain}`}>{fmtDate(doc.createdAt)}</div>
              </div>
            </div>
            {doc.status === 'approved' && doc.approvedAt && (
              <p className={`text-xs mt-3 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>✓ {t('ab.approvedOn', { date: fmtDate(doc.approvedAt) })}</p>
            )}
            {doc.status === 'rejected' && doc.rejectReason && (
              <p className={`text-xs mt-3 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('ab.rejectReasonLabel')}: {doc.rejectReason}</p>
            )}
          </Card>

          {/* Warnungen */}
          {docWarnings.length > 0 && (
            <Card isDarkMode={isDarkMode}>
              <SectionTitle isDarkMode={isDarkMode}>{t('ab.warningsTitle')}</SectionTitle>
              <div className="space-y-1.5">
                {docWarnings.map((w, i) => (
                  <p key={i} className={`text-xs flex items-start gap-1.5 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{w}
                  </p>
                ))}
              </div>
            </Card>
          )}

          {/* Positionstabelle */}
          <Card isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>{t('ab.linesTitle')}</SectionTitle>
            <AbPickHintBanner isDarkMode={isDarkMode} t={t} pickTarget={pickTarget} />
            <div className="mt-2">
              <AbLineTable
                isDarkMode={isDarkMode} t={t} lines={doc.lines} compareByLine={compareByLine}
                editable={editable} onSaveField={onSaveField} onDeleteLine={onDeleteLine} onAddLine={onAddLine}
                activeField={activeField} onActivateField={onActivateField} onDeactivateField={onDeactivateField}
                onHighlightField={onHighlightField} fillSignal={fillSignal}
              />
            </div>
          </Card>

          {/* Erkannter Rohtext (Diagnose, z. B. bei no_ruleset/failed) */}
          <AbRawTextPanel isDarkMode={isDarkMode} t={t} rawText={doc.rawText} />

          {/* Melde-Historie */}
          <AbReportHistory user={user} isDarkMode={isDarkMode} t={t} documentId={doc.id} />

          {/* Verlauf */}
          {events.length > 0 && (
            <Card isDarkMode={isDarkMode}>
              <SectionTitle isDarkMode={isDarkMode}>{t('ab.timeline')}</SectionTitle>
              <div className="space-y-0">
                {[...events].reverse().map((e) => (
                  <div key={e.id} className={`flex gap-3 py-2 border-b last:border-0 ${rowBorder}`}>
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      e.event?.includes('failed') || e.event === 'rejected' ? 'bg-red-500'
                        : e.event === 'approved' || e.event === 'sap_committed' || e.event === 'suggest_applied' ? 'bg-emerald-500'
                          : 'bg-indigo-500'
                    }`} />
                    <div className="min-w-0">
                      <div className={`text-sm ${textMain}`}>{eventLabel(t, e.event)}</div>
                      <div className={`text-[11px] ${textMuted}`}>{fmtDate(e.createdAt)}{e.actor ? ` · ${e.actor}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AbDocumentDetail;
